// src/components/ReservationWhatsAppModal.js
//
// Requerimiento "WhatsApp para Reservaciones y Lista de Espera".
// Modal: elegir destinatario (Cliente/Equipo/Ambos) → elegir template →
// previsualizar/editar → enviar. Reutiliza el mismo mecanismo de envío que
// ya usa Cierre Diario (ver src/utils/reservationWhatsapp.js) — este
// componente solo arma la UI, no reinventa cómo se manda el WhatsApp.
import React, { useEffect, useMemo, useState } from "react";
import { FaWhatsapp, FaTimes, FaHistory } from "react-icons/fa";
import {
  RESERVATION_WHATSAPP_TEMPLATES,
  buildReservationTemplateVars,
  fillTemplate,
  fetchReservationTeam,
  sendReservationWhatsapp,
  logReservationWhatsappHistory,
  fetchReservationWhatsappHistory,
} from "../utils/reservationWhatsapp";
import "../styles/ReservationWhatsAppModal.css";

const CLIENT_TEMPLATES = RESERVATION_WHATSAPP_TEMPLATES.filter((t) => t.target === "cliente");
const TEAM_TEMPLATES = RESERVATION_WHATSAPP_TEMPLATES.filter((t) => t.target === "equipo");

const ReservationWhatsAppModal = ({ item, property, onClose, onSent }) => {
  // Log de diagnóstico: si esto no aparece en consola al dar clic en el
  // botón de WhatsApp, el problema es que el JS desplegado no incluye este
  // componente (no llegó el build). Si SÍ aparece, el componente monta bien
  // y cualquier problema visual es de estilos (ya cubierto con inline styles
  // de respaldo abajo, independientes del CSS externo).
  console.log("🟢 ReservationWhatsAppModal MONTADO", { item, property });

  const hasPhone = !!item?.phone;

  const [recipient, setRecipient] = useState(hasPhone ? "cliente" : "equipo");
  const [clientTemplateId, setClientTemplateId] = useState(CLIENT_TEMPLATES[0]?.id || "");
  const [teamTemplateId, setTeamTemplateId] = useState(TEAM_TEMPLATES[0]?.id || "");
  const [clientMessage, setClientMessage] = useState("");
  const [teamMessage, setTeamMessage] = useState("");

  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [sendState, setSendState] = useState(null); // null | 'sending' | 'done' | 'error'
  const [sendSummary, setSendSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const vars = useMemo(() => buildReservationTemplateVars(item, property), [item, property]);

  // Cargar equipo activo de la propiedad al abrir el modal.
  useEffect(() => {
    let cancelled = false;
    setLoadingTeam(true);
    fetchReservationTeam(property).then((t) => {
      if (!cancelled) setTeam(t);
      setLoadingTeam(false);
    });
    return () => { cancelled = true; };
  }, [property]);

  // Al cambiar el template de cliente, regenerar el preview (el usuario
  // puede seguir editando libremente después — requerimiento punto 4).
  useEffect(() => {
    const tpl = CLIENT_TEMPLATES.find((t) => t.id === clientTemplateId);
    if (tpl) setClientMessage(fillTemplate(tpl.text, vars));
  }, [clientTemplateId, vars]);

  useEffect(() => {
    const tpl = TEAM_TEMPLATES.find((t) => t.id === teamTemplateId);
    if (tpl) setTeamMessage(fillTemplate(tpl.text, vars));
  }, [teamTemplateId, vars]);

  const loadHistory = () => {
    if (showHistory) { setShowHistory(false); return; }
    setShowHistory(true);
    setLoadingHistory(true);
    fetchReservationWhatsappHistory(property, item?.queue_id).then((h) => {
      setHistory(h);
      setLoadingHistory(false);
    });
  };

  const sendsCliente = recipient === "cliente" || recipient === "ambos";
  const sendsEquipo = recipient === "equipo" || recipient === "ambos";

  const canSend =
    sendState !== "sending" &&
    ((sendsCliente && hasPhone && clientMessage.trim()) || !sendsCliente) &&
    ((sendsEquipo && team.length > 0 && teamMessage.trim()) || !sendsEquipo) &&
    (sendsCliente || sendsEquipo);

  const handleSend = async () => {
    setErrorMsg("");

    if (sendsCliente && !hasPhone) {
      setErrorMsg("Este cliente no tiene un teléfono registrado.");
      return;
    }
    if (sendsEquipo && team.length === 0) {
      setErrorMsg("No hay números de equipo activos en teamsreservation.json para esta propiedad.");
      return;
    }

    setSendState("sending");
    const resultados = [];

    if (sendsCliente) {
      const r = await sendReservationWhatsapp({
        numeros: [item.phone],
        mensaje: clientMessage,
        property,
        tipo: `reservacion_${clientTemplateId}`,
      });
      resultados.push({ destinatario: "cliente", resultado: r });
      await logReservationWhatsappHistory({
        property,
        queueId: item?.queue_id,
        cliente: item?.customer_name,
        mensaje: clientMessage,
        destinatario: "cliente",
        estatus: r.success ? (r.fallidos > 0 ? "parcial" : "enviado") : "error",
        templateId: clientTemplateId,
      });
    }

    if (sendsEquipo) {
      const numerosEquipo = team.map((t) => t.telefono);
      const r = await sendReservationWhatsapp({
        numeros: numerosEquipo,
        mensaje: teamMessage,
        property,
        tipo: `reservacion_${teamTemplateId}`,
      });
      resultados.push({ destinatario: "equipo", resultado: r });
      await logReservationWhatsappHistory({
        property,
        queueId: item?.queue_id,
        cliente: item?.customer_name,
        mensaje: teamMessage,
        destinatario: "equipo",
        estatus: r.success ? (r.fallidos > 0 ? "parcial" : "enviado") : "error",
        templateId: teamTemplateId,
      });
    }

    const allOk = resultados.every((r) => r.resultado.success);
    setSendState(allOk ? "done" : "error");
    setSendSummary(resultados);
    if (allOk && onSent) onSent(resultados);
  };

  // Estilos en línea de respaldo: garantizan que el overlay/modal se vea
  // encima de TODO (incluyendo el modal de Lista de Espera, z-index 9999)
  // sin depender de que el archivo CSS externo haya cargado/desplegado bien.
  // Van además de las clases (no en lugar de) — si el CSS carga, esas reglas
  // (bordes redondeados, blur, etc.) se suman encima de esto sin conflicto.
  const inlineOverlayStyle = {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0, 0, 0, 0.6)",
    zIndex: 999999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const inlineModalStyle = {
    background: "#ffffff",
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    overflow: "auto",
    borderRadius: 16,
    boxShadow: "0 -8px 40px rgba(0,0,0,0.35)",
    margin: 16,
  };

  return (
    <div
      className="rwa-overlay"
      style={inlineOverlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="rwa-modal" style={inlineModalStyle}>
        <div className="rwa-header">
          <div className="rwa-header-icon"><FaWhatsapp /></div>
          <div className="rwa-header-text">
            <span className="rwa-title">Enviar WhatsApp</span>
            <span className="rwa-subtitle">{item?.customer_name || "Cliente"}{item?.party_size ? ` · ${item.party_size} personas` : ""}</span>
          </div>
          <button className="rwa-close-btn" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="rwa-body">
          {/* ── Destinatario ── */}
          <div className="rwa-section">
            <span className="rwa-section-label">Destinatario</span>
            <div className="rwa-recipient-group">
              <button
                type="button"
                className={`rwa-recipient-btn${recipient === "cliente" ? " rwa-recipient-btn--active" : ""}`}
                onClick={() => setRecipient("cliente")}
                disabled={!hasPhone}
                title={!hasPhone ? "Este registro no tiene teléfono" : undefined}
              >
                Cliente
              </button>
              <button
                type="button"
                className={`rwa-recipient-btn${recipient === "equipo" ? " rwa-recipient-btn--active" : ""}`}
                onClick={() => setRecipient("equipo")}
              >
                Equipo
              </button>
              <button
                type="button"
                className={`rwa-recipient-btn${recipient === "ambos" ? " rwa-recipient-btn--active" : ""}`}
                onClick={() => setRecipient("ambos")}
                disabled={!hasPhone}
                title={!hasPhone ? "Este registro no tiene teléfono" : undefined}
              >
                Cliente + Equipo
              </button>
            </div>
            {!hasPhone && (
              <p className="rwa-hint rwa-hint--warning">
                Este registro no tiene teléfono capturado — solo se puede notificar al equipo.
              </p>
            )}
          </div>

          {/* ── Mensaje al cliente ── */}
          {sendsCliente && (
            <div className="rwa-section">
              <span className="rwa-section-label">Mensaje al cliente</span>
              <select
                className="rwa-select"
                value={clientTemplateId}
                onChange={(e) => setClientTemplateId(e.target.value)}
              >
                {CLIENT_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <textarea
                className="rwa-textarea"
                value={clientMessage}
                onChange={(e) => setClientMessage(e.target.value)}
                rows={6}
              />
              <span className="rwa-hint">Se enviará a {item?.phone}</span>
            </div>
          )}

          {/* ── Mensaje al equipo ── */}
          {sendsEquipo && (
            <div className="rwa-section">
              <span className="rwa-section-label">Mensaje al equipo</span>
              <select
                className="rwa-select"
                value={teamTemplateId}
                onChange={(e) => setTeamTemplateId(e.target.value)}
              >
                {TEAM_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <textarea
                className="rwa-textarea"
                value={teamMessage}
                onChange={(e) => setTeamMessage(e.target.value)}
                rows={6}
              />
              <span className="rwa-hint">
                {loadingTeam
                  ? "Cargando equipo…"
                  : team.length > 0
                    ? `Se enviará a ${team.length} número(s): ${team.map((t) => t.nombre).join(", ")}`
                    : "⚠️ No hay números de equipo activos configurados para esta propiedad (teamsreservation.json)."}
              </span>
            </div>
          )}

          {errorMsg && <div className="rwa-error">{errorMsg}</div>}

          {sendState === "done" && (
            <div className="rwa-result rwa-result--ok">
              ✅ Mensaje(s) enviado(s).
              {sendSummary?.map((s, i) => (
                <div key={i} className="rwa-result-line">
                  {s.destinatario === "cliente" ? "Cliente" : "Equipo"}: {s.resultado.exitosos}/{s.resultado.total} entregado(s)
                </div>
              ))}
            </div>
          )}
          {sendState === "error" && (
            <div className="rwa-result rwa-result--error">
              ❌ Hubo un problema enviando uno o más mensajes. Revisa el historial para más detalle.
            </div>
          )}

          {/* ── Historial de este registro ── */}
          <div className="rwa-section">
            <button type="button" className="rwa-history-toggle" onClick={loadHistory}>
              <FaHistory /> {showHistory ? "Ocultar historial" : "Ver historial de notificaciones"}
            </button>
            {showHistory && (
              <div className="rwa-history-list">
                {loadingHistory ? (
                  <p className="rwa-hint">Cargando…</p>
                ) : history.length === 0 ? (
                  <p className="rwa-hint">Aún no se ha notificado a este cliente.</p>
                ) : (
                  history.map((h) => (
                    <div key={h.id} className={`rwa-history-item rwa-history-item--${h.estatus}`}>
                      <div className="rwa-history-item-top">
                        <span>{h.destinatario === "cliente" ? "📱 Cliente" : "👥 Equipo"}</span>
                        <span>{new Date(h.fecha).toLocaleString("es-MX")}</span>
                      </div>
                      <div className="rwa-history-item-msg">{h.mensaje}</div>
                      <div className="rwa-history-item-bottom">
                        <span>{h.usuario}</span>
                        <span className={`rwa-history-badge rwa-history-badge--${h.estatus}`}>{h.estatus}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rwa-footer">
          <button className="rwa-btn rwa-btn--cancel" onClick={onClose} type="button">
            <FaTimes /> Cerrar
          </button>
          <button
            className={`rwa-btn rwa-btn--send${!canSend ? " rwa-btn--disabled" : ""}`}
            onClick={handleSend}
            disabled={!canSend}
            type="button"
          >
            {sendState === "sending" ? (
              <>Enviando…</>
            ) : (
              <><FaWhatsapp /> Enviar</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReservationWhatsAppModal;
